import { createFileRoute, redirect } from '@tanstack/react-router';
import { ChevronDown, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { LANGUAGES } from '~/components/features/communications/constants';
import { InterviewMessageTemplateEditor } from '~/components/features/communications/interview-message-template-editor';
import {
  MessageTemplateEditor,
  type MessageType
} from '~/components/features/communications/message-template-editor';
import { DepartmentBreadcrumb } from '~/components/features/departments/department-breadcrumb';
import { PageContainer } from '~/components/layouts/page-container';
import { Button } from '~/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger
} from '~/components/ui/dropdown-menu';
import { useDocumentTitle } from '~/hooks/use-document-title';
import { useSlugIds } from '~/hooks/use-slug-ids';
import { communicationsQueryOptions } from '~/lib/communications';
import { resolveSlugIds } from '~/lib/slug-resolver';

const MESSAGE_TYPES = ['invite', 'reminder'] as const;
const INTERVIEW_TYPES = ['intro', 'outro'] as const;
const ALL_TYPES = [...MESSAGE_TYPES, ...INTERVIEW_TYPES] as const;

type InterviewType = (typeof INTERVIEW_TYPES)[number];
type TemplateType = (typeof ALL_TYPES)[number];

const typeSearchSchema = z.object({
  lang: z.string().length(2).default('en')
});

export const Route = createFileRoute(
  '/_authenticated/org/$orgSlug/department/$deptSlug/communications/$type'
)({
  validateSearch: typeSearchSchema,
  beforeLoad: ({ params }) => {
    if (!(ALL_TYPES as readonly string[]).includes(params.type)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        to: '/org/$orgSlug/department/$deptSlug/communications',
        params: { orgSlug: params.orgSlug, deptSlug: params.deptSlug }
      });
    }
  },
  loaderDeps: ({ search }) => ({ lang: search.lang }),
  loader: ({ context, params, deps }) => {
    const { orgId, deptId } = resolveSlugIds(context.queryClient, params.orgSlug, params.deptSlug);
    if (orgId && deptId) {
      void context.queryClient.prefetchQuery(communicationsQueryOptions(orgId, deptId, deps.lang));
    }
  },
  component: TemplateEditPage
});

const TITLE_KEYS = {
  invite: 'communications.invite.title',
  reminder: 'communications.reminder.title',
  intro: 'communications.intro.title',
  outro: 'communications.outro.title'
} as const satisfies Record<TemplateType, string>;

const DESC_KEYS = {
  invite: 'communications.invite.description',
  reminder: 'communications.reminder.description',
  intro: 'communications.intro.description',
  outro: 'communications.outro.description'
} as const satisfies Record<TemplateType, string>;

function TemplateEditPage(): React.ReactNode {
  const { t } = useTranslation();
  const { orgSlug, deptSlug, type } = Route.useParams();
  const { lang } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { orgId, deptId } = useSlugIds(orgSlug, deptSlug);

  const templateType = type as TemplateType;
  const isInterview = (INTERVIEW_TYPES as readonly string[]).includes(templateType);

  useDocumentTitle(`${t(TITLE_KEYS[templateType])} | ${t('communications.title')}`);

  return (
    <PageContainer
      breadcrumbs={[
        { content: <DepartmentBreadcrumb orgSlug={orgSlug} deptSlug={deptSlug} /> },
        {
          label: t('communications.title'),
          href: `/org/${orgSlug}/department/${deptSlug}/communications?lang=${lang}`
        },
        { label: t(TITLE_KEYS[templateType]) }
      ]}
      title={t(TITLE_KEYS[templateType])}
      subtitle={t(DESC_KEYS[templateType])}
      actions={
        <DropdownMenu>
          <DropdownMenuTrigger render={<Button variant="outline" />}>
            <Globe className="size-4" />
            {LANGUAGES.find((l) => l.code === lang)?.label ?? lang.toUpperCase()}
            <ChevronDown className="size-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuRadioGroup
              value={lang}
              onValueChange={(value: string) =>
                void navigate({ search: (_prev) => ({ lang: value }) })
              }
            >
              {LANGUAGES.map(({ code, label }) => (
                <DropdownMenuRadioItem key={code} value={code}>
                  {label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      }
    >
      {!orgId || !deptId ? (
        <div className="h-48 animate-pulse rounded-lg bg-muted" />
      ) : isInterview ? (
        <InterviewMessageTemplateEditor
          key={`${lang}-${templateType}`}
          orgId={orgId}
          deptId={deptId}
          templateType={templateType as InterviewType}
          lang={lang}
        />
      ) : (
        <MessageTemplateEditor
          key={`${lang}-${templateType}`}
          orgId={orgId}
          deptId={deptId}
          messageType={templateType as MessageType}
          lang={lang}
        />
      )}
    </PageContainer>
  );
}
